import { useSnackbar } from "notistack";
import React, { useEffect, useState, useCallback } from "react";
import useRouter from "use-react-router";

import { Organization } from "../../../dtos/organization";
import { SiteAggregate } from "../../../dtos/siteAggregate";
import { AjaxResult } from "../../../enums/ajaxResult";
import { HelpVideo } from "../../../enums/helpVideo";
import { useAuth } from "../../../features/auth/context";
import { useOrgSiteContext } from "../../../hooks/useOrgSiteContext";
import { PageHeader } from "../../PageHeader";
import { ButtonsContainer } from "../../shared/ButtonsContainer";
import { OrgSelect } from "../../shared/OrgSelect";
import { useApi } from "../../useApi";

import { AddSiteDialog, CommissionMode } from "./AddSiteDialog";
import { SitesExportButton } from "./SitesExportButton";
import { SitesTable } from "./table";

interface RouteState {
    serviceGuidFromParams: string | undefined;
    commissioningTokenFromQuery: string | undefined;
    openAddSiteDialog: boolean;
    commissionMode: CommissionMode;
}

export const SitesPage = () => {
    const { match, history } = useRouter();
    const params = (match.params as { guid: string });
    const query = new URLSearchParams(history.location.search);
    const token = query.get("token");

    const routeState = history.location.state as RouteState;
    const [serviceGuidFromParams, setServiceGuidFromParams] = useState<string | undefined>(
        (routeState && routeState.serviceGuidFromParams) || undefined
    );
    const [commissioningTokenFromQuery, setCommissioningTokenFromQuery] = useState<string | undefined>(
        (routeState && routeState.commissioningTokenFromQuery) || undefined
    );
    const [openAddSiteDialog, setOpenAddSiteDialog] = useState(
        (routeState && routeState.openAddSiteDialog) || false
    );
    const [commissionMode, setCommissionMode] = useState<CommissionMode>(
        (routeState && routeState.commissionMode) || "create"
    );
    const {
        dealerId,
        orgId,
        siteId: contextSiteId,
        siteChangeCallback,
    } = useOrgSiteContext<RouteState>(
        params.guid ? `sites/commission/${params.guid}?token=${token}` : `sites`,
        {
            serviceGuidFromParams: serviceGuidFromParams,
            commissioningTokenFromQuery: commissioningTokenFromQuery,
            openAddSiteDialog: openAddSiteDialog,
            commissionMode: commissionMode,
        },
    );
    const { enqueueSnackbar } = useSnackbar();
    const { siteApi, orgApi } = useApi();
    const {
        isGlobalAdmin,
        isDealerAdmin,
        isOrgAdmin,
        isOrgEmployee,
        isSiteAdmin,
        isSiteEmployee,
    } = useAuth();

    const viewIsReadOnly = !isGlobalAdmin && !isDealerAdmin;
    const deassocciateSiteFromContext = isOrgAdmin || isOrgEmployee || isSiteAdmin || isSiteEmployee;

    const [org, setOrg] = useState<Organization | null>(null);
    useEffect(() => {
        if (orgId) {
            orgApi.getById(orgId).then((r) => {
                if (r.result === AjaxResult.Success && r.data) {
                    setOrg(r.data);
                }
            });
        } else {
            setOrg(null);
        }
    }, [orgId, orgApi]);

    const [sites, setSites] = useState<SiteAggregate[]>([]);
    const [sitesAreLoading, setSitesAreLoading] = useState(false);

    const loadSites = useCallback(() => {
        if (contextSiteId && orgId) {
            setSitesAreLoading(true);
            siteApi.getSitesAggregateForSite(orgId, contextSiteId).then((r) => {
                if (r.result === AjaxResult.Success && r.data) {
                    setSites(r.data);
                } else {
                    enqueueSnackbar("ERROR: Failed to get sites from the server");
                }
                setSitesAreLoading(false);
            });
        } else if (orgId) {
            setSitesAreLoading(true);
            siteApi.getSitesAggregateForOrg(orgId).then((r) => {
                if (r.result === AjaxResult.Success && r.data) {
                    setSites(r.data);
                } else {
                    enqueueSnackbar("ERROR: Failed to get sites from the server");
                }
                setSitesAreLoading(false);
            });
        } else if (dealerId) {
            setSitesAreLoading(true);
            siteApi.getForDealerAggregate(dealerId).then((r) => {
                if (r.result === AjaxResult.Success && r.data) {
                    setSites(r.data);
                } else {
                    enqueueSnackbar("ERROR: Failed to get sites from the server");
                }
                setSitesAreLoading(false);
            });
        } else {
            setSites([]);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [orgId, contextSiteId, siteApi, dealerId]);

    useEffect(() => {
        loadSites();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [orgId, contextSiteId, siteApi, dealerId]);

    const handleCloseAddSiteDialog = useCallback(() => {
        setOpenAddSiteDialog(false)
        setCommissionMode("create");
        history.push(history.location.pathname.replace(/\/commission\/.*/, ""));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);


    const handleSiteDelete = useCallback((id: number) => {
        loadSites();
        if (id === contextSiteId) {
            siteChangeCallback(dealerId, orgId, null)
        }
    }, [loadSites, siteChangeCallback, dealerId, orgId, contextSiteId]);

    useEffect(() => {
        if (params.guid) {
            setServiceGuidFromParams(params.guid);
            setCommissioningTokenFromQuery(token || undefined);
            setOpenAddSiteDialog(true);
        }
    }, [params.guid, token]);

    return (
        <>
            <PageHeader
                helpVideoNum={HelpVideo.Site}
                isLoading={sitesAreLoading}
            >
                {`${org ? org.name : ""} `}Sites
            </PageHeader>
            <OrgSelect
                selectedOrgId={orgId || null}
                selectedDealerId={dealerId || null}
                selectedSiteId={contextSiteId || null}
                onChange={(val: {
                    dealerId?: number | null;
                    orgId?: number | null;
                    siteId?: number | null;
                }) => siteChangeCallback(val.dealerId, val.orgId, val.siteId)}
                bypassSiteContext={deassocciateSiteFromContext}
            />
            {viewIsReadOnly ? null : (
                <ButtonsContainer>
                    {!contextSiteId && (
                        <SitesExportButton
                            orgId={orgId}
                            dealerId={dealerId}
                        />
                    )}
                </ButtonsContainer>
            )}

            <SitesTable
                sites={sites}
                sitesAreLoading={sitesAreLoading}
                contextDealerId={dealerId}
                contextOrgId={orgId}
                onDeleteSite={handleSiteDelete}
                onLoadSites={loadSites}
            />

            <AddSiteDialog
                isOpen={openAddSiteDialog}
                onClose={handleCloseAddSiteDialog}
                onSiteAdded={loadSites}
                serviceGuidFromParams={serviceGuidFromParams}
                commissioningTokenFromQuery={commissioningTokenFromQuery}
                orgId={orgId}
                mode={commissionMode}
                onModeChange={setCommissionMode}
                existingSiteId={commissionMode === "rebind" ? (contextSiteId || undefined) : undefined}
            >
                {serviceGuidFromParams && (
                    <OrgSelect
                        selectedOrgId={orgId || null}
                        selectedDealerId={dealerId || null}
                        selectedSiteId={contextSiteId || null}
                        onChange={(val: {
                            dealerId?: number | null;
                            orgId?: number | null;
                            siteId?: number | null;
                        }) => siteChangeCallback(val.dealerId, val.orgId, val.siteId)}
                        siteSelectIsHidden={commissionMode === "create"}
                        bypassSiteContext={deassocciateSiteFromContext}
                        descriptive={true}
                        isDealerRequired={true}
                        isOrganizationRequired={true}
                    />
                )}
            </AddSiteDialog>
        </>
    );
};
